import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Container,
  Form,
  Button,
  Message,
  Grid,
  Image,
  Divider,
  Icon
} from 'semantic-ui-react';
import { useAuth } from '../../context/AuthContext';
import { resolvePostLoginPath } from '../../utils/homeRedirect';
import { LoginRequest } from '../../types/api';
import GettelLogo from '../../images/logo-gettel.png';
import LoginFooter from '../../components/Layout/LoginFooter';
import Footer from '../../components/Layout/Footer';

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  const [formData, setFormData] = useState<LoginRequest>({
    usernameOrEmail: '',
    password: ''
  });
  const [lockoutMessage, setLockoutMessage] = useState<string | null>(null);
  const [deviceNotAuthorizedMessage, setDeviceNotAuthorizedMessage] = useState<string | null>(null);


  // Se já está autenticado, redirecionar
  if (isAuthenticated) {
    const from = location.state?.from?.pathname as string | undefined;
    const target = resolvePostLoginPath(from, user?.roles ?? []);
    return <Navigate to={target} replace />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLockoutMessage(null);
    setDeviceNotAuthorizedMessage(null);

    try {
      await login(formData);
    } catch (error: unknown) {
      const err = error as {
        isAccountLockout?: boolean;
        isDeviceNotAuthorized?: boolean;
        message?: string;
      };
      if (err?.isAccountLockout && err.message) {
        setLockoutMessage(err.message);
      } else if (err?.isDeviceNotAuthorized && err.message) {
        setDeviceNotAuthorizedMessage(err.message);
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 50%, #87CEEB 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Container textAlign="center">
          <div style={{
            background: 'rgba(230, 230, 250, 0.1)',
            borderRadius: '20px',
            padding: '40px 30px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 15px 35px rgba(176, 196, 222, 0.3)',
            backdropFilter: 'blur(10px)'
          }}>
            <Image
              src={GettelLogo}
              centered
              size="medium"
              style={{
                filter: 'brightness(0) invert(1)',
                marginBottom: '20px'
              }}
            />
            <div style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '300',
              letterSpacing: '1px'
            }}>
              Carregando...
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #2a2a2a 100%)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <Container>
          <Grid textAlign="center" verticalAlign="middle">
            <Grid.Column style={{ maxWidth: 500 }}>
            {/* Logo Section */}
            <div style={{
              background: 'rgba(230, 230, 250, 0.1)',
              borderRadius: '20px 20px 0 0',
              padding: '40px 30px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 15px 35px rgba(176, 196, 222, 0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <Image
                src={GettelLogo}
                centered
                size="medium"
                style={{
                  filter: 'brightness(0) invert(1)',
                  marginBottom: '20px'
                }}
              />
              <h2 style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '300',
                margin: '0',
                letterSpacing: '2px',
                textTransform: 'uppercase'
              }}>
                Sistema de Gestão
              </h2>
            </div>

            {/* Form Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '0 0 20px 20px',
              padding: '40px 30px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderTop: 'none',
              boxShadow: '0 15px 35px rgba(176, 196, 222, 0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              {lockoutMessage && (
                <Message
                  negative
                  role="alert"
                  style={{
                    background: 'rgba(192, 57, 43, 0.25)',
                    border: '1px solid rgba(231, 76, 60, 0.5)',
                    color: '#ffffff',
                    borderRadius: '10px',
                    marginBottom: '20px'
                  }}
                >
                  <Message.Header style={{ color: '#ffffff' }}>
                    Conta bloqueada
                  </Message.Header>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{lockoutMessage}</p>
                </Message>
              )}

              {deviceNotAuthorizedMessage && (
                <Message
                  negative
                  role="alert"
                  style={{
                    background: 'rgba(192, 57, 43, 0.25)',
                    border: '1px solid rgba(231, 76, 60, 0.5)',
                    color: '#ffffff',
                    borderRadius: '10px',
                    marginBottom: '20px'
                  }}
                >
                  <Message.Header style={{ color: '#ffffff' }}>
                    Acesso negado
                  </Message.Header>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{deviceNotAuthorizedMessage}</p>
                </Message>
              )}

              <Form size="large" onSubmit={handleSubmit}>
                <div style={{ position: 'relative', marginBottom: '1em' }}>
                  <Form.Input
                    fluid
                    placeholder="E-mail ou Usuário"
                    type="text"
                    name="usernameOrEmail"
                    value={formData.usernameOrEmail}
                    onChange={handleInputChange}
                    required
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      fontSize: '16px',
                      borderRadius: '8px',
                      padding: '12px 16px 12px 45px'
                    }}
                  />
                  <Icon
                    name="user"
                    style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#fff',
                      zIndex: 1000
                    }}
                  />
                </div>

                <div style={{ position: 'relative', marginBottom: '1em' }}>
                  <Form.Input
                    fluid
                    placeholder="Senha"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      fontSize: '16px',
                      borderRadius: '8px',
                      padding: '12px 16px 12px 45px'
                    }}
                  />
                  <Icon
                    name="lock"
                    style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#fff',
                      zIndex: 1000
                    }}
                  />
                </div>

                <Divider style={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  margin: '30px 0'
                }} />

                <Button
                  fluid
                  size="large"
                  type="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  style={{
                    background: 'linear-gradient(135deg, #00d4aa 0%, #00b8a0 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '15px',
                    fontSize: '16px',
                    fontWeight: '600',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    boxShadow: '0 8px 25px rgba(0, 212, 170, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 212, 170, 0.4)';
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 170, 0.3)';
                  }}
                >
                  Entrar
                </Button>
              </Form>

              <Message
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  borderRadius: '10px',
                  marginTop: '30px'
                }}
              >
                <Message.Header style={{ color: '#ffffff' }}>
                  Primeiro Acesso
                </Message.Header>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Entre em contato com o administrador do sistema para
                  obter suas credenciais de acesso.
                </p>
              </Message>
            </div>
          </Grid.Column>
        </Grid>
      </Container>
      </div>

      {/* Footer pequeno para verificação do Facebook Meta Developer */}
      <LoginFooter />
      
      {/* Rodapé alinhado ao tema da página de login */}
      <Footer />
    </div>
  );
};

export default LoginPage;
